import React from 'react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import GuardianAuth from '@amzn/GuardianAuth/lib/auth/GuardianAuth';
import asyncRequest from '@amzn/GuardianWidgetCommons/lib/helper/asyncRequest';
import { AppThunk } from '../../context/store';
import { toast } from 'react-toastify';
import {
    GET_EMAIL_NOTIFICATIONS_GROUPS_ENDPOINT,
    ADD_EMAIL_NOTIFICATIONS_GROUP_ENDPOINT,
    UPDATE_EMAIL_NOTIFICATIONS_GROUP_ENDPOINT
} from '../../lib/globals';
import ToastErrorMessage from '@amzn/GuardianWidgetCommons/lib/components/ToastErrorMessage';
import { EmailGroupType } from '../../lib/types';

const { doRequest } = asyncRequest;

interface EventTypes {
    open: boolean;
    close: boolean;
    reopen: boolean;
    end: boolean;
}

interface EmailNotificationGroup {
    id: number;
    name: string;
    extId: string;
    groupType: EmailGroupType;
    openEvent: number;
    closeEvent: number;
    reopenEvent: number;
    endEvent: number;
    updatedBy: string;
    lastUpdated: string;
}

interface AddEmailNotificationGroupRequest {
    name: string;
    extId: string;
    groupType: EmailGroupType;
    events: EventTypes;
    updatedBy: string;
}

interface UpdateEmailNotificationGroupRequest {
    id: number;
    events: EventTypes;
}

interface EmailNotificationsState {
    emailNotificationsGroups: EmailNotificationGroup[];
    isGettingEmailNotificationsGroups: boolean;
    isAddingEmailNotificationGroup: boolean;
    isUpdatingEmailNotificationGroup: boolean;
    getEmailNotificationsGroupsError: string;
    addEmailNotificationsGroupError: string;
    updateEmailNotificationsGroupError: string;
}

export const initialState: EmailNotificationsState = {
    emailNotificationsGroups: [],
    isGettingEmailNotificationsGroups: false,
    isAddingEmailNotificationGroup: false,
    isUpdatingEmailNotificationGroup: false,
    getEmailNotificationsGroupsError: '',
    addEmailNotificationsGroupError: '',
    updateEmailNotificationsGroupError: ''
};

const emailNotificationsSlice = createSlice({
    name: 'emailNotifications',
    initialState,
    reducers: {
        startGetEmailNotificationsGroups: (state) => {
            state.emailNotificationsGroups = initialState.emailNotificationsGroups;
            state.isGettingEmailNotificationsGroups = true;
            state.getEmailNotificationsGroupsError = initialState.getEmailNotificationsGroupsError;
        },
        startAddEmailNotificationGroup: (state) => {
            state.isAddingEmailNotificationGroup = true;
            state.addEmailNotificationsGroupError = initialState.addEmailNotificationsGroupError;
        },
        startUpdateEmailNotificationGroup: (state) => {
            state.isUpdatingEmailNotificationGroup = true;
            state.updateEmailNotificationsGroupError = initialState.updateEmailNotificationsGroupError;
        },
        getEmailNotificationsGroupsSucceeded: (
            state,
            { payload }: PayloadAction<{ emailNotificationsGroups: EmailNotificationGroup[] }>
        ) => {
            const { emailNotificationsGroups } = payload;
            state.isGettingEmailNotificationsGroups = false;
            state.emailNotificationsGroups = emailNotificationsGroups;
        },
        addEmailNotificationGroupSucceeded: (state) => {
            state.isAddingEmailNotificationGroup = false;
        },
        updateEmailNotificationGroupSucceeded: (state) => {
            state.isUpdatingEmailNotificationGroup = false;
        },
        getEmailNotificationGroupsFailed: (state, { payload }: PayloadAction<{ error: string }>) => {
            const { error } = payload;
            state.isGettingEmailNotificationsGroups = false;
            state.getEmailNotificationsGroupsError = error;
        },
        addEmailNotificationGroupFailed: (state, { payload }: PayloadAction<{ error: string }>) => {
            const { error } = payload;
            state.isAddingEmailNotificationGroup = false;
            state.addEmailNotificationsGroupError = error;
        },
        updateEmailNotificationGroupFailed: (state, { payload }: PayloadAction<{ error: string }>) => {
            const { error } = payload;
            state.isUpdatingEmailNotificationGroup = false;
            state.updateEmailNotificationsGroupError = error;
        }
    }
});

export const {
    startGetEmailNotificationsGroups,
    startAddEmailNotificationGroup,
    startUpdateEmailNotificationGroup,
    getEmailNotificationsGroupsSucceeded,
    addEmailNotificationGroupSucceeded,
    updateEmailNotificationGroupSucceeded,
    getEmailNotificationGroupsFailed,
    addEmailNotificationGroupFailed,
    updateEmailNotificationGroupFailed
} = emailNotificationsSlice.actions;

export default emailNotificationsSlice.reducer;

export const getEmailNotificationsGroupsAsync = (): AppThunk => async (dispatch) => {
    try {
        dispatch(startGetEmailNotificationsGroups());
        const response = await doRequest(
            GET_EMAIL_NOTIFICATIONS_GROUPS_ENDPOINT,
            {},
            GuardianAuth.createRequestAuthHeader()
        );
        const emailNotificationsGroupsResult = response?.data;
        dispatch(
            getEmailNotificationsGroupsSucceeded({ emailNotificationsGroups: emailNotificationsGroupsResult })
        );
    } catch (error) {
        dispatch(getEmailNotificationGroupsFailed({ error: error.message }));
        toast.error(
            <ToastErrorMessage
                header={'Failed to retrieve list of email notifications groups'}
                errorMessage={error.message}
            />
        );
    }
};

export const addEmailNotificationGroupAsync = (request: AddEmailNotificationGroupRequest): AppThunk => async (
    dispatch
) => {
    const toastId = toast.info('Adding email notification group...');
    try {
        dispatch(startAddEmailNotificationGroup());
        await doRequest(
            ADD_EMAIL_NOTIFICATIONS_GROUP_ENDPOINT,
            request,
            GuardianAuth.createRequestAuthHeader()
        );
        dispatch(addEmailNotificationGroupSucceeded());
        toast.update(toastId, {
            render: 'Successfully added email notification group',
            type: toast.TYPE.SUCCESS
        });
        dispatch(getEmailNotificationsGroupsAsync());
    } catch (error) {
        dispatch(addEmailNotificationGroupFailed({ error: error.message }));
        toast.update(toastId, {
            render: (
                <ToastErrorMessage
                    header={'Failed to add email notification group'}
                    errorMessage={error.message}
                />
            ),
            type: toast.TYPE.ERROR,
            autoClose: false
        });
    }
};

export const updateEmailNotificationGroupAsync = (
    request: UpdateEmailNotificationGroupRequest
): AppThunk => async (dispatch) => {
    const toastId = toast.info('Updating email notification group...');
    try {
        dispatch(startUpdateEmailNotificationGroup());
        await doRequest(
            UPDATE_EMAIL_NOTIFICATIONS_GROUP_ENDPOINT,
            request,
            GuardianAuth.createRequestAuthHeader()
        );
        dispatch(updateEmailNotificationGroupSucceeded());
        toast.update(toastId, {
            render: 'Successfully updated email notification group',
            type: toast.TYPE.SUCCESS
        });
        dispatch(getEmailNotificationsGroupsAsync());
    } catch (error) {
        dispatch(updateEmailNotificationGroupFailed({ error: error.message }));
        toast.update(toastId, {
            render: (
                <ToastErrorMessage
                    header={'Failed to update email notification group'}
                    errorMessage={error.message}
                />
            ),
            type: toast.TYPE.ERROR,
            autoClose: false
        });
    }
};
